import React, { useEffect, useState } from 'react';
import './DataRequestDashboardPage.scss';
import Layout from '../../../components/Layout';
import { Badge, Breadcrumb, BreadcrumbItem, Card } from 'reactstrap';
import { HomeButton } from '../../../components/HomeButton/home-button';
import { useStateSelector } from '../../../store/selectors';
import { isViewAsBuyerActive } from '../../../utils/viewAsBuyer';
import createNotification from '../../../utils/createNotification';
import DocumentsRequestForm from '../DocumentsRequestForm/DocumentsRequestForm';
import {
    DataRequestProject,
    DataRequestProjectState,
} from '../../../models/DataRequestHub/DataRequestProject';
import { useLocation, useNavigate } from 'react-router-dom';
import { objectToQueryString } from '../../../utils/queryHelper';
import { ProjectTypeEnum } from '../../../models/DataRequestHub/ProjectTypeEnum';
import { useSearchParams } from 'react-router-dom';
import { DataFormType } from '../../../models/DataRequestHub/DataFormTypeEnum';
import DataLoader from '../../../components/DataLoader/DataLoader';
import QuestionnaireStagesHost from '../QuestionnaireStagesHost/QuestionnaireStagesHost';
import TabNotificationStatus from '../../../models/DataRequestHub/TabNotificationStatus';
import { useDispatch } from 'react-redux';
import DataTableStagesHost from '../DataTableStagesHost/DataTableStagesHost';
import { EditorTypeEnum } from '../../../models/DataRequestHub/ProjectEditorEnum';
import { NotificationFlag } from '../../../slices/notification-flags/notification-flag';
import { updateNotificationFlagCountByCode } from '../../../slices/notification-flags/notification-flags.slice';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';
import { CapvarSupportRequestWrapper } from './CapvarSupportRequestWrapper/CapvarSupportRequestWrapper';
import { initializeDataRequestNotifications } from '../../../models/DataRequestHub/slices/thunks/initialize-data-request-notifications.thunk';
import { updateQuestionsAndCells } from '../../../models/DataRequestHub/slices/thunks/update-questions-and-cells.thunk';
import { resetQuestionsSeenStatusByFormType } from '../../../models/DataRequestHub/slices/thunks/reset-questions-seen-status-by-form-type.thunk';
import {
    putFormSeenFlag,
    putQuestionsSeenFlag,
    putViewSeenFlag,
} from '../../../models/DataRequestHub/slices/thunks/put-seen-flag.thunks';
import {
    planFirstOpenQuestionnaireCalls,
    planFirstOpenViewCalls,
    planFormTypeSeenCalls,
} from '../../../models/DataRequestHub/slices/seen-flag-call-planner';
import { FreezePanesSettings } from '../../../models/DataRequestHub/slices/models/freeze-panes-settings.model';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { canMarkDataRequestItemsAsSeen } from '../../../utils/canMarkDataRequestItemsAsSeen';

interface DataRequestDashboardProps {
    projectId?: number;
    isBuiltInMode?: boolean;
}

interface TabData {
    formType: EditorTypeEnum;
    displayName: string;
}

export const DataRequestDashboardPage = (props: DataRequestDashboardProps) => {
    const isBuiltInMode = useStateSelector(
        (s) => s.appConfigs.values.isBuiltInMode
    );
    const dispatch: any = useDispatch();
    const questionsSeenStatus = useStateSelector(
        (s) => s.dataRequestsNotifications.questionsSeenStatus
    );
    const tabsNotificationStatus = useStateSelector(
        (s) => s.dataRequestsNotifications.projectTabsNotificationsStatus
    );
    const formsNotificationsStatus = useStateSelector(
        (s) => s.dataRequestsNotifications.formsNotificationsStatus
    );
    const axios = useStateSelector((s) => s.core.axios);
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const [isAdminView, setIsAdminView] = useState(
        location.state?.isAdmin || params.get('isAdmin') === 'true'
    );
    const [canUseDataTools, setCanUseDataTools] = useState(
        location.state?.canUseDataTools ??
            (params.get('canUseDataTools') !== null
                ? params.get('canUseDataTools') === 'true'
                : location.state?.isAdmin || params.get('isAdmin') === 'true')
    );
    const [isReadOnly] = useState(params.get('isReadOnly') === 'true');
    const [preselectedQuestionnaireFormId, setPreselectedQuestionnaireFormId] =
        useState(0);
    const [preselectedTableId, setPreselectedTableId] = useState(0);
    const [preselectedTableViewId, setPreselectedTableViewId] = useState(0);
    const [projectName, setProjectType] = useState('');
    const [projectId, setProjectId] = useState('');
    const [practiceName, setPracticeName] = useState('');
    const [projects, setProjects] = useState<DataRequestProject[]>([]);
    const [activeTab, setActiveTab] = useState<EditorTypeEnum>(null);
    const [isExternalProject, setIsExternalProject] = useState(false);
    const [isSingleitemProject, setIsSingleitemProject] = useState(false);
    const [engagementId, setEngagementId] = useState<number | undefined>(undefined);
    const party = useCurrentMarketplaceParty(engagementId);
    // Outside an engagement this dashboard is only reachable by users the
    // seen-flag endpoints accept (project owner / affiliated members / admins).
    // Inside one, read-only parties (advisor, observer, buyer) must not send
    // seen flags — the API rejects their writes.
    const canSendSeenFlags =
        !isViewAsBuyerActive() &&
        (!engagementId || canMarkDataRequestItemsAsSeen(party));
    const [searchParams] = useSearchParams({});
    const [tabData] = useState<TabData[]>([
        { formType: EditorTypeEnum.Documents, displayName: 'Documents' },
        {
            formType: EditorTypeEnum.FinancialRequest,
            displayName: 'Financial Data',
        },
        {
            formType: EditorTypeEnum.Questionnaire,
            displayName: 'Questionnaires',
        },
        {
            formType: EditorTypeEnum.DataTable,
            displayName: 'Data Tables',
        },
    ]);

    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const isAdminView = location.state?.isAdmin ?? false;
        let projectId = location.state?.projectId;

        if (isAdminView) {
            setIsAdminView(true);
        }

        if (!location.state?.projectId && params.get('projectId')) {
            projectId = params.get('projectId') || '';
            setProjectId(projectId);
        }

        if (!location.state?.isAdmin && params.get('isAdmin')) {
            setIsAdminView(params.get('isAdmin') === 'true');
        }

        if (location.state?.canUseDataTools !== undefined) {
            setCanUseDataTools(!!location.state.canUseDataTools);
        } else if (params.get('canUseDataTools') !== null) {
            setCanUseDataTools(params.get('canUseDataTools') === 'true');
        } else {
            setCanUseDataTools(
                location.state?.isAdmin || params.get('isAdmin') === 'true'
            );
        }

        if (!location.state?.projectName && params.get('projectName')) {
            setProjectType(params.get('projectName') || '');
        }

        if (
            !location.state?.isExternalProject &&
            params.get('isExternalProject')
        ) {
            setIsExternalProject(params.get('isExternalProject') === 'true');
        } else {
            const isExternalProject =
                location.state?.isExternalProject ?? false;
            setIsExternalProject(isExternalProject);
        }

        if (!location.state?.questionnaireId && params.get('questionnaireId')) {
            setPreselectedQuestionnaireFormId(
                Number(params.get('questionnaireId'))
            );
        } else {
            const questionnaireId =
                location.state?.preselectedQuestionnaireFormId ?? 0;
            setPreselectedQuestionnaireFormId(questionnaireId);
        }

        const projectName = location.state?.projectName ?? 'Other';
        if (projectName) {
            setProjectType(projectName);
        }

        const practiceName = location.state?.practiceName ?? '';
        if (practiceName) {
            setPracticeName(practiceName);
        }

        const isSingleitemProject =
            location.state?.isSingleitemProject ?? false;
        setIsSingleitemProject(isSingleitemProject);

        const tableId = location.state?.preselectedTableId ?? 0;

        setPreselectedTableId(tableId);

        const tableViewId = location.state?.preselectedTableViewId ?? 0;
        setPreselectedTableViewId(tableViewId);

        const engagementIdParam = location.state?.engagementId ?? params.get('engagementId');
        if (engagementIdParam) {
            setEngagementId(Number(engagementIdParam));
        }

        fetchData(true, projectId);
    }, []);

    useEffect(() => {
        const value = searchParams.get('tab');
        const numberValue = Number(value);
        if (value && numberValue && numberValue >= 1 && numberValue <= 4) {
            const index = numberValue - 1;
            const dataForms = projects
                .flatMap((x) => x.dataForms)
                .filter(
                    (x) =>
                        x.isVisible || x.formType === DataFormType.Questionnaire
                );

            if (
                !dataForms.some((x) => x.formType == index) &&
                !(index === EditorTypeEnum.Questionnaire) &&
                !(index === EditorTypeEnum.DataTable)
            ) {
                setFirstAvailableTab();
            } else {
                setActiveTab(index);
                const type = getDataFormType(tabData[index].formType);
                markFormAsSeen(type);
            }
        } else if (projects.length) {
            setFirstAvailableTab();
        }
    }, [projects]);

    const getNotificationFlagCode = (): string => {
        if (projects.length && tabsNotificationStatus.length) {
            if (projectId) {
                const targetProject = projects.find((f) => f);
                const practiceId = targetProject?.practiceId ?? '';
                const projectId = targetProject?.id ?? '';

                return `${NotificationFlagsCategories.PracticesCode}.${practiceId}.DR.${projectId}.NOT_REMOVABLE`;
            }

            return NotificationFlagsCategories.OtherDataRequests;
        }

        return '';
    };

    useEffect(() => {
        if (!isAdminView) {
            const tabsNotificationCount = tabsNotificationStatus.reduce(
                (a, b) => a + b.countOfNotifications,
                0
            );
            const updatedFlag: NotificationFlag = {
                eventCode: getNotificationFlagCode(),
                count: tabsNotificationCount,
            };

            dispatch(updateNotificationFlagCountByCode(updatedFlag));
        }
    }, [tabsNotificationStatus]);

    const setFirstAvailableTab = () => {
        const dataForms = projects
            .flatMap((x) => x.dataForms)
            .filter((x) => x.isVisible);
        const formTypes = dataForms.map((form) => form?.formType);
        const firstAvailableFormType = Math.min(
            ...formTypes,
            EditorTypeEnum.DataTable
        );
        setActiveTab(firstAvailableFormType);

        const type = getDataFormType(tabData[firstAvailableFormType]?.formType);
        markFormAsSeen(type);
    };

    const changeTab = (index: number) => {
        const tabNumber = index + 1;
        setActiveTab((lastActiveTab) => {
            if (
                lastActiveTab !== index &&
                lastActiveTab != EditorTypeEnum.Questionnaire &&
                lastActiveTab != EditorTypeEnum.DataTable
            ) {
                dispatch(
                    resetQuestionsSeenStatusByFormType({
                        formTypeToReset: Number(lastActiveTab),
                    })
                );
            }
            return index;
        });
        const type = getDataFormType(tabData[index].formType);
        markFormAsSeen(type);

        navigate(`${location.pathname}?tab=${tabNumber}`, {
            state: {
                projectId: projectId,
                isAdmin: isAdminView,
                projectName: projectName,
                practiceName: practiceName,
            },
        });
    };

    const fetchData = (
        isInitialFetch: boolean = false,
        paramsProjectId: string = undefined
    ) => {
        const projId =
            location.state?.projectId ??
            props.projectId ??
            paramsProjectId ??
            projectId;
        if (projId) {
            setProjectId(projId);
            fetchProjectData(projId, isInitialFetch);
        } else {
            fetchProjectsWithOtherType(isInitialFetch);
        }
    };

    const initializeQuestionsSeenStatus = (projects: DataRequestProject[]) => {
        dispatch(
            initializeDataRequestNotifications({
                projects: projects,
                isAdminView: isAdminView,
            })
        );
    };

    const markQuestionsAsSeen = (
        formId: number,
        questionsIds: number[],
        subQuesitonsIds: number[]
    ) => {
        if (!canSendSeenFlags) return;

        const targetProject = projects.find((project) => {
            const targetForm = project.dataForms.find((f) => f.id === formId);
            return targetForm;
        });
        const targetForm = targetProject.dataForms.find((f) => f.id === formId);

        if ((!targetProject.isSeenByUser || targetForm.isNew) && !isAdminView) {
            dispatch(
                putFormSeenFlag({ projectId: targetProject.id, formId: formId })
            );
        } else {
            dispatch(
                putQuestionsSeenFlag({
                    projectId: targetProject.id,
                    formId: formId,
                    questionsIds: questionsIds,
                    subQuestionsIds: subQuesitonsIds,
                })
            );
        }
    };

    const markCellsAsSeen = (
        projectId: number,
        tableId: number,
        viewId: number
    ) => {
        if (!canSendSeenFlags) return;

        if (tableId && viewId) {
            dispatch(
                putViewSeenFlag({
                    projectId: projectId,
                    tableId: tableId,
                    viewId: viewId,
                })
            );
        }
    };

    const updateColumnWidth = (
        tableId: number,
        columnId: string,
        width: number
    ) => {
        setProjects((prevProjects) =>
            prevProjects.map((project) => ({
                ...project,
                dataTables: project.dataTables.map((table) => {
                    if (table.id !== tableId) return table;
                    return {
                        ...table,
                        columns: table.columns.map((col) =>
                            col.customColumnId === columnId
                                ? { ...col, width }
                                : col
                        ),
                        views: table.views.map((view) => ({
                            ...view,
                            viewColumns: view.viewColumns.map((vc) =>
                                vc.customColumnId === columnId
                                    ? { ...vc, width }
                                    : vc
                            ),
                        })),
                    };
                }),
            }))
        );
    };

    const updateFreezePanes = (
        tableId: number,
        viewId: number,
        settings: FreezePanesSettings
    ) => {
        setProjects((prevProjects) =>
            prevProjects.map((project) => ({
                ...project,
                dataTables: project.dataTables.map((table) => {
                    if (table.id !== tableId) return table;
                    return {
                        ...table,
                        views: table.views.map((view) =>
                            view.id === viewId
                                ? { ...view, freezePanesSettings: settings }
                                : view
                        ),
                    };
                }),
            }))
        );
    };

    const updateDataTableFreezePanes = (
        tableId: number,
        settings: FreezePanesSettings
    ) => {
        setProjects((prevProjects) =>
            prevProjects.map((project) => ({
                ...project,
                dataTables: project.dataTables.map((table) => {
                    if (table.id !== tableId) return table;
                    return {
                        ...table,
                        freezePanesSettings: settings,
                    };
                }),
            }))
        );
    };

    // One seen-flag call per never-opened project is enough — the backend resets the whole
    // project's seen state on the first call — so these planners collapse the old per-form
    // request loops and skip forms with nothing left to clear.
    const resetQuestionnaireFormsIfNewProject = () => {
        planFirstOpenQuestionnaireCalls(projects).forEach((call) =>
            dispatch(putFormSeenFlag(call))
        );
    };

    const resetDataTableViewsIfNewProject = () => {
        planFirstOpenViewCalls(projects).forEach((call) =>
            dispatch(putViewSeenFlag(call))
        );
    };

    const markFormAsSeen = (formTypeToMarkAsSeen: DataFormType) => {
        if (!canSendSeenFlags) return;

        if (formTypeToMarkAsSeen === DataFormType.DataTable) {
            resetDataTableViewsIfNewProject();
        }

        if (formTypeToMarkAsSeen === DataFormType.Questionnaire) {
            resetQuestionnaireFormsIfNewProject();
        } else {
            planFormTypeSeenCalls(
                projects,
                formTypeToMarkAsSeen,
                formsNotificationsStatus
            ).forEach((call) => dispatch(putFormSeenFlag(call)));
        }
    };

    const fetchProjectData = (projectId: number, isInitialFetch: boolean) => {
        axios
            .get(`/api/dataRequestProjects/${projectId}`)
            .then((response) => {
                if (response.status === 200) {
                    if (isInitialFetch) {
                        initializeQuestionsSeenStatus([{ ...response.data }]);
                    } else {
                        dispatch(
                            updateQuestionsAndCells({
                                projects: [{ ...response.data }],
                            })
                        );
                    }

                    setProjects([{ ...response.data }]);
                } else {
                    createNotification(
                        (response as any).response?.data?.detail ??
                            'An Error occured while fetching project data',
                        'error'
                    );
                }
            })
            .finally(() => setIsLoading(false));
    };

    const fetchProjectsWithOtherType = (isInitialFetch: boolean) => {
        axios
            .get(
                `/api/dataRequestProjects?${objectToQueryString({
                    projectType: ProjectTypeEnum.Other,
                })}`
            )
            .then((response) => {
                if (response.status) {
                    setProjects([...response.data]);
                    if (isInitialFetch) {
                        initializeQuestionsSeenStatus(response.data);
                    } else {
                        dispatch(
                            updateQuestionsAndCells({
                                projects: [...response.data],
                            })
                        );
                    }
                } else {
                    createNotification(
                        'An Error occured while fetching project data',
                        'error'
                    );
                }
            })
            .finally(() => setIsLoading(false));
    };

    const getHeaderTitle = () => {
        if (isExternalProject) {
            return projectName;
        }
        const practiceLalel = practiceName
            ? ` - Practice: ${practiceName}`
            : projects.length == 1
            ? ` - Practice: ${projects.find((x) => x).practiceName}`
            : '';
        return `${projectName}` + practiceLalel;
    };

    const getCurrentPracticeId = () => {
        const practiceId = projects.find((x) => x).practiceId;
        return practiceId;
    };

    const isSupportRequestButtonVisible = () => {
        const project = projects.find((x) => x);
        if (
            !isAdminView &&
            projects.length === 1 &&
            project.projectType === ProjectTypeEnum.CapvarDataInput
        ) {
            return true;
        }
        return false;
    };

    useEffect(() => {
        if (isAdminView) {
            const refreshProjectInfoInterval = setInterval(() => {
                fetchData();
            }, 10000);

            return () => {
                clearInterval(refreshProjectInfoInterval);
            };
        }
    }, [isAdminView, projectName]);

    const tabContent = () => {
        switch (activeTab) {
            case EditorTypeEnum.Documents:
                return (
                    <DocumentsRequestForm
                        key={'document-view-form'}
                        formType={DataFormType.Documents}
                        isAdminView={isAdminView}
                        canUseDataTools={canUseDataTools}
                        isReadOnly={isReadOnly}
                        projects={projects}
                        onSuccessfulUpload={fetchData}
                        updateProjectsData={fetchData}
                        markAsSeen={() =>
                            markFormAsSeen(DataFormType.Documents)
                        }
                        questionsSeenStatus={questionsSeenStatus}
                        engagementId={engagementId}
                    />
                );
            case EditorTypeEnum.FinancialRequest:
                return (
                    <DocumentsRequestForm
                        key={'financial-request-view-form'}
                        formType={DataFormType.FinancialRequest}
                        isAdminView={isAdminView}
                        canUseDataTools={canUseDataTools}
                        isReadOnly={isReadOnly}
                        projects={projects}
                        onSuccessfulUpload={fetchData}
                        updateProjectsData={fetchData}
                        markAsSeen={() =>
                            markFormAsSeen(DataFormType.FinancialRequest)
                        }
                        questionsSeenStatus={questionsSeenStatus}
                        engagementId={engagementId}
                    />
                );
            case EditorTypeEnum.Questionnaire:
                return (
                    <QuestionnaireStagesHost
                        preselectedQuestionnaireFormId={
                            preselectedQuestionnaireFormId
                        }
                        projects={projects}
                        isAdminView={isAdminView}
                        canUseDataTools={canUseDataTools}
                        isReadOnly={isReadOnly}
                        updateProjectsData={fetchData}
                        questionsSeenStatus={questionsSeenStatus}
                        markQuestionsAsSeen={markQuestionsAsSeen}
                        isSingleitemProject={isSingleitemProject}
                        isBuiltIn={isBuiltInMode || props.isBuiltInMode}
                        engagementId={engagementId}
                    />
                );
            case EditorTypeEnum.DataTable:
                return (
                    <DataTableStagesHost
                        projects={projects}
                        preselectedTableId={preselectedTableId}
                        preselectedTableViewId={preselectedTableViewId}
                        isAdminView={isAdminView}
                        canUseDataTools={canUseDataTools}
                        isReadonlyMode={isReadOnly}
                        updateProjectsData={fetchData}
                        markCellsAsSeen={markCellsAsSeen}
                        updateColumnWidth={updateColumnWidth}
                        updateFreezePanes={updateFreezePanes}
                        updateDataTableFreezePanes={updateDataTableFreezePanes}
                        engagementId={engagementId}
                    />
                );
            default:
                return <></>;
        }
    };

    const getTabClasses = (index: number) => {
        const isTabVisible = isTabPrepared(index);

        if (!isTabVisible) {
            return 'RRT__tab d-none';
        }

        return activeTab == index
            ? 'RRT__tab RRT__tab--first RRT__tab--selected'
            : 'RRT__tab';
    };

    const isTabPrepared = (index: number) => {
        return projects.some((x) => {
            return (
                x.dataForms.some(
                    (form) => form.formType == index && form.isVisible
                ) ||
                (index === EditorTypeEnum.Questionnaire &&
                    isAdminView &&
                    x.state === DataRequestProjectState.Active) ||
                (index === EditorTypeEnum.DataTable &&
                    ((isAdminView &&
                        x.state === DataRequestProjectState.Active) ||
                        x.dataTables.length))
            );
        });
    };

    const getDataFormType = (type: EditorTypeEnum) => {
        switch (type) {
            case EditorTypeEnum.Documents:
                return DataFormType.Documents;
            case EditorTypeEnum.FinancialRequest:
                return DataFormType.FinancialRequest;
            case EditorTypeEnum.Questionnaire:
                return DataFormType.Questionnaire;
            case EditorTypeEnum.DataTable:
                return DataFormType.DataTable;
            default:
                return DataFormType.Documents;
        }
    };

    const getTabNotificationState = (
        type: EditorTypeEnum
    ): TabNotificationStatus => {
        const tabFormType = getDataFormType(type);
        return (
            tabsNotificationStatus.find((f) => f.formType === tabFormType) ?? {
                displayName: '',
                formType: tabFormType,
                isNewForm: false,
                countOfNotifications: 0,
                isNewProject: true,
                formId: 0,
            }
        );
    };

    const pageContent = () => (
        <Card className="main-card">
            <div className="body_white_box practice-page-tabs m-0">
                <div className={isBuiltInMode ? '' : 'white-box-content'}>
                    {isLoading ? (
                        <DataLoader />
                    ) : (
                        <>
                            <div className="RRT__container">
                                <div className="RRT__tabs body-tabs">
                                    {tabData
                                        ?.filter(
                                            (tab) =>
                                                tab.formType !==
                                                    EditorTypeEnum.DataTable ||
                                                !isSingleitemProject
                                        )
                                        .map((item, index) => {
                                            const tabState =
                                                getTabNotificationState(
                                                    item.formType
                                                );
                                            return (
                                                <div
                                                    key={index}
                                                    className={getTabClasses(
                                                        index
                                                    )}
                                                    onClick={() => {
                                                        changeTab(index);
                                                    }}
                                                    id={'tab-info-' + index}>
                                                    {item.displayName}
                                                    {tabState.countOfNotifications &&
                                                    !tabState.isNewProject ? (
                                                        <Badge className="unread-comments-badge">
                                                            {tabState.countOfNotifications >
                                                            9
                                                                ? '9+'
                                                                : tabState.countOfNotifications}
                                                        </Badge>
                                                    ) : (
                                                        <></>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                            {tabContent()}
                        </>
                    )}
                </div>
            </div>
        </Card>
    );

    return !isBuiltInMode && !props.isBuiltInMode ? (
        <Layout>
            <div className="data-request-dashboard">
                <div className="page-title">
                    <div className="title-content">
                        <div className="page-title">
                            <h3>{getHeaderTitle()}</h3>
                        </div>
                        <Breadcrumb>
                            <BreadcrumbItem>
                                <HomeButton></HomeButton>
                            </BreadcrumbItem>
                            <BreadcrumbItem active>
                                Data Request Dashboard
                            </BreadcrumbItem>
                        </Breadcrumb>
                    </div>
                    {isSupportRequestButtonVisible() && (
                        <CapvarSupportRequestWrapper
                            practiceId={getCurrentPracticeId()}></CapvarSupportRequestWrapper>
                    )}
                </div>
                {pageContent()}
            </div>
        </Layout>
    ) : (
        <div className="data-request-dashboard data-request-dashboard-built-in">
            {pageContent()}
        </div>
    );
};
